/**
 * Question Template Manager
 * Allows consultants to customize discovery questions for themes and CI categories
 * Connected to questionTemplateRoutes.tsx (5 endpoints)
 */

import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  ChevronDown, 
  ChevronRight, 
  HelpCircle,
  Loader2,
  FileQuestion,
  AlertCircle
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';

interface QuestionTemplate {
  id: string;
  text: string;
  description?: string;
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'boolean' | 'number';
  required: boolean;
  category?: string;
  questionType?: string;
  priority?: 'required' | 'recommended' | 'optional';
  options?: Array<{ value: string; label: string; description?: string }>;
  helpText?: string;
  examples?: string[];
  conditionalOn?: {
    questionId: string;
    expectedValue: any;
  };
  createdBy?: string;
  createdAt?: string;
  customized?: boolean;
}

interface QuestionTemplateSet {
  level: 'theme' | 'domain' | 'category';
  id: string;
  name: string;
  questions: QuestionTemplate[];
  createdAt: string;
  lastUpdated: string;
  customizedBy?: string;
}

const THEME_OPTIONS = [
  { id: 'hlo', name: 'High Level Objectives' },
  { id: 'team-structure', name: 'Team Structure & Spaces' },
  { id: 'asset-management', name: 'Asset Management (CMDB)' },
  { id: 'service-requests', name: 'Service Request Management' },
  { id: 'change-management', name: 'Change Management' },
  { id: 'incident-management', name: 'Incident Management' },
  { id: 'automation', name: 'Automation' },
];

const CATEGORY_OPTIONS = [
  { id: 'service_request_type', name: 'Service Request Type' },
  { id: 'workflow', name: 'Workflow' },
];

export function QuestionTemplateManager({
  companyId,
  accessToken
}: {
  companyId: string;
  accessToken: string;
}) {
  const [selectedType, setSelectedType] = useState<'theme' | 'category'>('theme');
  const [selectedId, setSelectedId] = useState<string>('hlo');
  const [templateSet, setTemplateSet] = useState<QuestionTemplateSet | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState<Partial<QuestionTemplate>>({
    text: '',
    type: 'text',
    required: false,
    priority: 'recommended',
  });

  useEffect(() => {
    loadTemplateSet();
  }, [selectedType, selectedId]);

  const loadTemplateSet = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const selectedOption = selectedType === 'theme' 
        ? THEME_OPTIONS.find(t => t.id === selectedId)
        : CATEGORY_OPTIONS.find(c => c.id === selectedId);
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/question-templates/${selectedType}/${selectedId}?name=${encodeURIComponent(selectedOption?.name || selectedId)}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load question templates');
      }

      const data = await response.json();
      setTemplateSet(data.templateSet);
    } catch (err: any) {
      console.error('Error loading templates:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/question-templates/${selectedType}/${selectedId}/add-question`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add question');
      }

      const data = await response.json();
      setTemplateSet(data.templateSet);
      setAddingNew(false);
      setFormData({
        text: '',
        type: 'text',
        required: false,
        priority: 'recommended',
      });
    } catch (err: any) {
      console.error('Error adding question:', err);
      alert(`Failed to add question: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuestion = async (questionId: string) => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/question-templates/${selectedType}/${selectedId}/update-question/${questionId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update question');
      }

      const data = await response.json();
      setTemplateSet(data.templateSet);
      setEditingQuestion(null);
      setFormData({
        text: '',
        type: 'text',
        required: false,
        priority: 'recommended',
      });
    } catch (err: any) {
      console.error('Error updating question:', err);
      alert(`Failed to update question: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!confirm('Are you sure you want to delete this question?')) return;

    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/question-templates/${selectedType}/${selectedId}/delete-question/${questionId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete question');
      }

      const data = await response.json();
      setTemplateSet(data.templateSet);
    } catch (err: any) {
      console.error('Error deleting question:', err);
      alert(`Failed to delete question: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (question: QuestionTemplate) => {
    setEditingQuestion(question.id);
    setFormData({
      text: question.text,
      description: question.description,
      type: question.type,
      required: question.required,
      category: question.category,
      priority: question.priority,
      helpText: question.helpText,
      examples: question.examples,
    });
  };

  const cancelEdit = () => {
    setEditingQuestion(null);
    setAddingNew(false);
    setFormData({
      text: '',
      type: 'text',
      required: false,
      priority: 'recommended',
    });
  };

  const toggleExpanded = (questionId: string) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'required':
        return 'bg-red-100 text-red-800';
      case 'recommended':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading && !templateSet) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="size-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">Failed to load templates: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-gray-900">Question Template Manager</h3>
        <p className="text-gray-600 mt-1">
          Customize discovery questions to improve your consulting process over time
        </p>
      </div>

      {/* Selector */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-700 mb-2">Template Type</label>
          <select
            value={selectedType}
            onChange={(e) => {
              setSelectedType(e.target.value as 'theme' | 'category');
              setSelectedId(e.target.value === 'theme' ? 'hlo' : 'service_request_type');
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="theme">Discovery Theme</option>
            <option value="category">CI Category</option>
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-2">Select {selectedType === 'theme' ? 'Theme' : 'Category'}</label>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            {selectedType === 'theme' ? (
              THEME_OPTIONS.map(theme => (
                <option key={theme.id} value={theme.id}>{theme.name}</option>
              ))
            ) : (
              CATEGORY_OPTIONS.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))
            )}
          </select>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <FileQuestion className="size-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <div className="text-blue-900">
            {templateSet?.questions.length || 0} questions in this template
          </div>
          <div className="text-sm text-blue-700 mt-1">
            {templateSet?.customizedBy ? 'Customized by your firm' : 'Using default questions'}
          </div>
        </div>
      </div>

      {/* Questions List */}
      <div className="space-y-2">
        {templateSet?.questions.map((question, index) => (
          <div
            key={question.id}
            className={`border rounded-lg overflow-hidden ${
              question.customized ? 'border-purple-300 bg-purple-50' : 'border-gray-200 bg-white'
            }`}
          >
            <div className="p-4">
              {editingQuestion === question.id ? (
                // Edit Mode
                <div className="space-y-3">
                  <input
                    type="text"
                    value={formData.text}
                    onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                    placeholder="Question text"
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                      className="px-3 py-2 border border-gray-300 rounded"
                    >
                      <option value="text">Text</option>
                      <option value="textarea">Textarea</option>
                      <option value="select">Select</option>
                      <option value="boolean">Yes/No</option>
                      <option value="number">Number</option>
                    </select>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                      className="px-3 py-2 border border-gray-300 rounded"
                    >
                      <option value="required">Required</option>
                      <option value="recommended">Recommended</option>
                      <option value="optional">Optional</option>
                    </select>
                  </div>
                  <textarea
                    value={formData.helpText || ''}
                    onChange={(e) => setFormData({ ...formData, helpText: e.target.value })}
                    placeholder="Help text (optional)"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.required}
                        onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                      />
                      <span className="text-sm text-gray-700">Required</span>
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleUpdateQuestion(question.id)}
                      disabled={loading}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                    >
                      <Save className="size-4" />
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div>
                  <div className="flex items-start justify-between">
                    <button
                      onClick={() => toggleExpanded(question.id)}
                      className="flex items-start gap-2 flex-1 text-left"
                    >
                      {expandedQuestions[question.id] ? (
                        <ChevronDown className="size-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      ) : (
                        <ChevronRight className="size-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <div className="text-gray-900">{index + 1}. {question.text}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 text-xs rounded ${getPriorityColor(question.priority)}`}>
                            {question.priority}
                          </span>
                          <span className="text-xs text-gray-600">
                            {question.type}
                          </span>
                          {question.required && (
                            <span className="text-xs text-red-600">*required</span>
                          )}
                          {question.customized && (
                            <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded">
                              Custom
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                    <div className="flex items-center gap-1 ml-2">
                      <button
                        onClick={() => startEditing(question)}
                        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                      >
                        <Edit2 className="size-4" />
                      </button>
                      {question.customized && (
                        <button
                          onClick={() => handleDeleteQuestion(question.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedQuestions[question.id] && (
                    <div className="mt-3 ml-7 space-y-2 text-sm">
                      {question.helpText && (
                        <div className="flex items-start gap-2">
                          <HelpCircle className="size-4 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="text-gray-700">{question.helpText}</div>
                        </div>
                      )}
                      {question.examples && question.examples.length > 0 && (
                        <div>
                          <div className="text-gray-600 mb-1">Examples:</div>
                          <ul className="list-disc list-inside text-gray-700 space-y-1">
                            {question.examples.map((ex, i) => (
                              <li key={i}>{ex}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {question.conditionalOn && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-900">
                          Conditional: Only shown if question {question.conditionalOn.questionId} = {JSON.stringify(question.conditionalOn.expectedValue)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add New Question */}
      {addingNew ? (
        <div className="border border-purple-300 rounded-lg p-4 bg-purple-50 space-y-3">
          <h4 className="text-gray-900">Add New Question</h4>
          <input
            type="text"
            value={formData.text}
            onChange={(e) => setFormData({ ...formData, text: e.target.value })}
            placeholder="Question text"
            className="w-full px-3 py-2 border border-gray-300 rounded"
          />
          <div className="grid grid-cols-2 gap-3">
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
              className="px-3 py-2 border border-gray-300 rounded"
            >
              <option value="text">Text</option>
              <option value="textarea">Textarea</option>
              <option value="select">Select</option>
              <option value="boolean">Yes/No</option>
              <option value="number">Number</option>
            </select>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
              className="px-3 py-2 border border-gray-300 rounded"
            >
              <option value="required">Required</option>
              <option value="recommended">Recommended</option>
              <option value="optional">Optional</option>
            </select>
          </div>
          <textarea
            value={formData.helpText || ''}
            onChange={(e) => setFormData({ ...formData, helpText: e.target.value })}
            placeholder="Help text (optional)"
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded"
          />
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.required}
                onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
              />
              <span className="text-sm text-gray-700">Required</span>
            </label>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleAddQuestion}
              disabled={loading || !formData.text}
              className="px-3 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
            >
              <Save className="size-4" />
              Add Question
            </button>
            <button
              onClick={cancelEdit}
              className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAddingNew(true)}
          className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-all flex items-center justify-center gap-2"
        >
          <Plus className="size-5" />
          Add Custom Question
        </button>
      )}
    </div>
  );
}
